import React from 'react';
import { formatNumberWithCommas } from '../../utilities';

const formatDate = (timestamp) =>
  new Date(timestamp * 1000)
    .toLocaleString('en-GB', {
      timeZone: 'Asia/Dhaka',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
    .replace(/\//g, '.');

// Generic inline-editable wrapper. `as` lets you pick the tag (span/div/td/etc).
const EditableField = ({ editMode, as: Tag = 'span', style, children, ...rest }) => (
  <Tag
    contentEditable={editMode}
    suppressContentEditableWarning={true}
    style={{
      outline: editMode ? '1px dashed #3b82f6' : 'none',
      backgroundColor: editMode ? 'rgba(59,130,246,0.06)' : 'transparent',
      minWidth: editMode ? '10px' : undefined,
      cursor: editMode ? 'text' : 'inherit',
      ...style,
    }}
    {...rest}
  >
    {children}
  </Tag>
);

const DeliveryChallanStatement = ({ commercial_info, challan_info, editMode = false }) => {
  const { grandQty, grandValue, grandOrderQty } = challan_info.reduce(
    (acc, challan) => {
      challan.items.forEach((item) => {
        item.gsms.forEach((gsm) => {
          acc.grandQty += Number(gsm.challanQty) || 0;
          acc.grandValue += Number(gsm.totalAmount) || 0;
          acc.grandOrderQty += Number(gsm.orderQty) || 0;
        });
      });
      return acc;
    },
    { grandQty: 0, grandValue: 0, grandOrderQty: 0 }
  );

  const thStyle = {
    border: '1px solid black',
    padding: '6px 8px',
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    textAlign: 'center',
    fontSize: '13px',
  };

  const tdStyle = {
    border: '1px solid black',
    padding: '0',
    textAlign: 'center',
    fontSize: '13px',
  };

  const tdInnerStyle = {
    padding: '4px 8px',
  };

  // Specific style applied only to the Style/PO column cells
  const stylePoColumnStyle = {
    width: '110px',
    maxWidth: '110px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  };

  return (
    <div style={{ display: 'block', fontSize: '11px' }}>
      <style>{`
        @media print {
          [contenteditable] {
            outline: none !important;
            background-color: transparent !important;
          }
        }
      `}</style>

      <div style={{ textAlign: 'center' }}>
        <EditableField
          editMode={editMode}
          as="h1"
          style={{
            textDecoration: 'underline',
            fontWeight: 'bold',
            marginTop: '5px',
            marginBottom: '10px',
            fontSize: '23px',
            color: '#000',
            display: 'inline-block',
          }}
        >
          DELIVERY CHALLAN STATEMENT
        </EditableField>
        <EditableField
          editMode={editMode}
          as="h3"
          style={{ fontWeight: 'bold', marginBottom: '10px', display: 'block', textAlign: 'center' }}
        >
          {commercial_info.partyName}
        </EditableField>
        <EditableField
          editMode={editMode}
          as="div"
          style={{ fontSize: '13px', marginBottom: '15px', display: 'inline-block' }}
        >
          LC: {commercial_info.lcNo}, Date: {formatDate(commercial_info.lcDate)}
          {commercial_info.amendmentInfo ? `, Amendment No: ${commercial_info.amendmentInfo}` : ''}
          {commercial_info.amendmentDate ? `, Date: ${formatDate(commercial_info.amendmentDate)}` : ''}
          , Value: ${formatNumberWithCommas(grandValue.toFixed(2))}
        </EditableField>
      </div>

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        className="cmp-table"
        // This table is tighter than the challan/invoice ones, so it declares its own
        // base vertical paddings for the Row spacing control to scale from. Keep these
        // in sync with tdInnerStyle / thStyle above.
        style={{ border: '1px solid #000', '--cmp-pad': '4px', '--cmp-pad-th': '6px' }}
      >
        <thead>
          <tr>
            <th style={{ ...thStyle, width: '16%' }}>Item Name</th>
            <th style={{ ...thStyle, width: '110px' }}>Style/PO</th>
            <th style={thStyle}>Ply</th>
            <th style={thStyle}>Measurement</th>
            <th style={thStyle}>Order Qty</th>
            <th style={thStyle}>Challan</th>
            <th style={thStyle}>Date</th>
            <th style={thStyle}>Challan Qty</th>
            <th style={thStyle}>Unit Price</th>
            <th style={thStyle}>Total Amount</th>
          </tr>
        </thead>
        <tbody>
          {challan_info.map((challan, challanIndex) => {
            const challanRowSpanLength = challan.items.reduce((acc, item) => acc + item.gsms.length, 0);

            const challanRows = challan.items.map((item, itemIndex) => {
              const itemRowSpanLength = item.gsms.length;

              return item.gsms.map((gsm, gsmIndex) => (
                <tr key={`${challanIndex}-${itemIndex}-${gsmIndex}`}>
                  {gsmIndex === 0 && (
                    <>
                      <td rowSpan={itemRowSpanLength} style={tdStyle}>
                        <EditableField editMode={editMode} as="div" style={{ ...tdInnerStyle, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                          {item.itemName}
                        </EditableField>
                      </td>
                      <td rowSpan={itemRowSpanLength} style={{ ...tdStyle, ...stylePoColumnStyle }}>
                        <EditableField editMode={editMode} as="div" style={{ ...tdInnerStyle, ...stylePoColumnStyle }}>
                          {item.stylePo}
                        </EditableField>
                      </td>
                    </>
                  )}
                  <td style={tdStyle}>
                    <EditableField editMode={editMode} as="div" style={tdInnerStyle}>
                      {gsm.ply}
                    </EditableField>
                  </td>
                  <td style={tdStyle}>
                    <EditableField editMode={editMode} as="div" style={tdInnerStyle}>
                      {gsm.measurement.split('\n').map((line, lineIndex) => (
                        <React.Fragment key={lineIndex}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                    </EditableField>
                  </td>
                  <td style={tdStyle}>
                    <EditableField editMode={editMode} as="div" style={tdInnerStyle}>
                      {gsm.orderQty}
                    </EditableField>
                  </td>
                  {itemIndex === 0 && gsmIndex === 0 && (
                    <>
                      <td rowSpan={challanRowSpanLength} style={tdStyle}>
                        <EditableField editMode={editMode} as="div" style={tdInnerStyle}>
                          {challan.deliveryChallanNo}
                        </EditableField>
                      </td>
                      <td rowSpan={challanRowSpanLength} style={tdStyle}>
                        <EditableField editMode={editMode} as="div" style={tdInnerStyle}>
                          {formatDate(challan.deliveryDate)}
                        </EditableField>
                      </td>
                    </>
                  )}
                  <td style={tdStyle}>
                    <EditableField editMode={editMode} as="div" style={tdInnerStyle}>
                      {gsm.challanQty} Pcs
                    </EditableField>
                  </td>
                  <td style={tdStyle}>
                    <EditableField editMode={editMode} as="div" style={tdInnerStyle}>
                      ${gsm.unitPrice}
                    </EditableField>
                  </td>
                  <td style={tdStyle}>
                    <EditableField editMode={editMode} as="div" style={tdInnerStyle}>
                      ${formatNumberWithCommas(Number(gsm.totalAmount).toFixed(2))}
                    </EditableField>
                  </td>
                </tr>
              ));
            });

            return <React.Fragment key={challanIndex}>{challanRows}</React.Fragment>;
          })}
          <tr>
            <td colSpan={4} style={tdStyle}>
              <strong>
                <EditableField editMode={editMode} style={{ ...tdInnerStyle, display: 'inline-block' }}>
                  TOTAL
                </EditableField>
              </strong>
            </td>
            <td style={tdStyle}>
              <strong>
                <EditableField editMode={editMode} style={{ ...tdInnerStyle, display: 'inline-block' }}>
                  {grandOrderQty}
                </EditableField>
              </strong>
            </td>
            <td style={tdStyle}></td>
            <td style={tdStyle}></td>
            <td style={tdStyle}>
              <strong>
                <EditableField editMode={editMode} style={{ ...tdInnerStyle, display: 'inline-block' }}>
                  {grandQty}
                </EditableField>
              </strong>
            </td>
            <td style={tdStyle}></td>
            <td style={tdStyle}>
              <strong>
                <EditableField editMode={editMode} style={{ ...tdInnerStyle, display: 'inline-block' }}>
                  ${formatNumberWithCommas(grandValue.toFixed(2))}
                </EditableField>
              </strong>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default DeliveryChallanStatement;